<div class="room" [class.compact]="compact">
  <audio #audio id="meeting-audio" autoplay></audio>

  <header class="topbar">
    <div class="meeting-id">
      <span class="brand-mark"><img src="assets/SCRAII_LoginLogo.svg" alt="SCRAII" /></span>
      <div class="title-wrap">
        <h1 [title]="title">{{ title }}</h1>
        <span class="secure">
          <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.shield" [attr.d]="d"></path></svg>
          Secure Meeting
        </span>
      </div>
    </div>

    <div class="presence">
      <div class="stack" *ngIf="people.length" aria-hidden="true">
        <span class="stack-avatar" *ngFor="let person of stackPeople; trackBy: trackPerson" [style.background]="color(person).color" [title]="nameOf(person)">
          {{ initials(person) }}
        </span>
      </div>
      <span class="in-count">{{ people.length || 1 }} in Meeting</span>
      <span class="timer" role="timer">
        <span class="rec-dot" *ngIf="elapsed" aria-hidden="true"></span>
        {{ elapsed || "Connecting…" }}
      </span>
    </div>
  </header>

  <div class="banners" *ngIf="sharing || reconnecting || poorConnection || shareError">
    <div class="banner presenting-bar" *ngIf="sharing">
      <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.share" [attr.d]="d"></path></svg>
      <span>You're presenting your screen</span>
      <button type="button" (click)="share()">Stop presenting</button>
    </div>
    <div class="banner warn" *ngIf="reconnecting">
      <span class="spinner small"></span>
      <span>Reconnecting…</span>
    </div>
    <div class="banner warn" *ngIf="poorConnection && !reconnecting">
      <mat-icon>signal_cellular_alt</mat-icon>
      <span>Your network quality may be affecting the call.</span>
    </div>
    <div class="banner error" *ngIf="shareError">
      <mat-icon>error_outline</mat-icon>
      <span>{{ shareError }}</span>
      <button type="button" class="icon-btn" (click)="shareError = ''" aria-label="Dismiss">
        <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.close" [attr.d]="d"></path></svg>
      </button>
    </div>
  </div>

  <div class="body">
    <main class="stage-card">
      <div class="stage" #stage>
        <div
          class="tile content-tile"
          *ngIf="content as shared"
          [ngStyle]="tileStyles[contentId]"
          [class.hidden]="isHidden(contentId)"
          [class.viewing]="!shared.local"
          [class.zoomed]="contentZoom > 1"
          [class.dragging]="contentDragging"
          (wheel)="onContentWheel($event)"
          (dblclick)="onContentDblClick($event)"
          (pointerdown)="onContentPointerDown($event)"
          (pointermove)="onContentPointerMove($event)"
          (pointerup)="onContentPointerUp($event)"
          (pointercancel)="onContentPointerUp($event)">
          <!-- Zoom and rotation go on this wrapper: Chime resets the <video>'s own transform when it binds a stream. -->
          <div class="content-view" *ngIf="!shared.local" [ngStyle]="contentViewStyle">
            <video [id]="'tile-' + shared.tileId" autoplay playsinline [muted]="true"></video>
          </div>
          <div class="presenting" *ngIf="shared.local">
            <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.share" [attr.d]="d"></path></svg>
            <strong>You're sharing your screen</strong>
            <span>Everyone in the meeting can see it.</span>
            <button type="button" (click)="share()">Stop presenting</button>
          </div>
          <div class="content-bar">
            <span class="nameplate">
              <span class="np-icons">
                <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.share" [attr.d]="d"></path></svg>
              </span>
              <span class="np-divider" aria-hidden="true"></span>
              <span class="np-name">{{ contentLabel }}</span>
            </span>
            <div class="view-tools" *ngIf="!shared.local" role="toolbar" aria-label="Shared screen view">
              <button type="button" (click)="zoomContent(-1)" [disabled]="contentZoom <= 1" matTooltip="Zoom out" aria-label="Zoom out">
                <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.zoomOut" [attr.d]="d"></path></svg>
              </button>
              <button
                type="button"
                class="zoom-level"
                (click)="resetContentView()"
                [disabled]="!contentViewChanged"
                matTooltip="Reset view"
                [attr.aria-label]="'Zoom ' + contentZoomLabel + '. Reset view'">
                {{ contentZoomLabel }}
              </button>
              <button type="button" (click)="zoomContent(1)" [disabled]="contentZoom >= contentZoomMax" matTooltip="Zoom in" aria-label="Zoom in">
                <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.zoomIn" [attr.d]="d"></path></svg>
              </button>
              <span class="np-divider" aria-hidden="true"></span>
              <button type="button" (click)="rotateContent(-1)" matTooltip="Rotate left" aria-label="Rotate left">
                <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.rotateLeft" [attr.d]="d"></path></svg>
              </button>
              <button type="button" (click)="rotateContent(1)" matTooltip="Rotate right" aria-label="Rotate right">
                <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.rotateRight" [attr.d]="d"></path></svg>
              </button>
            </div>
          </div>
        </div>

        <div
          class="tile"
          *ngFor="let person of people; trackBy: trackPerson"
          [ngStyle]="tileStyles[person.attendeeId]"
          [class.hidden]="isHidden(person.attendeeId)"
          [class.floating]="rect(person.attendeeId)?.floating"
          [class.main]="rect(person.attendeeId)?.main"
          [class.strip]="rect(person.attendeeId)?.strip"
          [class.small]="isSmall(person.attendeeId)"
          [class.large]="isLarge(person.attendeeId)"
          [class.local]="person.local"
          [class.has-video]="showVideo(person)"
          [class.speaking]="person.speaking"
          [class.hand]="person.handRaisedAt != null">
          <video *ngIf="person.tileId != null" [id]="'tile-' + person.tileId" [class.off]="!showVideo(person)" autoplay playsinline [muted]="true"></video>
          <div class="avatar-wrap" *ngIf="!showVideo(person)">
            <span class="halo" aria-hidden="true"><i></i><i></i><i></i></span>
            <span class="avatar" [style.background]="color(person).color">{{ initials(person) }}</span>
          </div>
          <span class="hand-badge" *ngIf="person.handRaisedAt != null" aria-label="Hand raised">✋</span>
          <span class="tile-reaction" *ngIf="person.reaction" aria-hidden="true">{{ person.reaction }}</span>
          <div class="nameplate">
            <span class="np-icons">
              <svg class="ico" *ngIf="!audioOnly" viewBox="0 0 24 24" [attr.aria-label]="showVideo(person) ? 'Camera on' : 'Camera off'" role="img" focusable="false">
                <path *ngFor="let d of (showVideo(person) ? icons.video : icons.videoOff)" [attr.d]="d"></path>
              </svg>
              <svg class="ico" viewBox="0 0 24 24" [attr.aria-label]="person.muted === true ? 'Muted' : 'Microphone on'" role="img" focusable="false">
                <path *ngFor="let d of (person.muted === true ? icons.micOff : icons.mic)" [attr.d]="d"></path>
              </svg>
              <svg class="ico" *ngIf="pinnedId === person.attendeeId" viewBox="0 0 24 24" aria-label="Pinned" role="img" focusable="false">
                <path *ngFor="let d of icons.pin" [attr.d]="d"></path>
              </svg>
            </span>
            <span class="np-divider" aria-hidden="true"></span>
            <span class="np-name">{{ plateName(person) }}</span>
          </div>
          <button
            type="button"
            class="tile-more"
            [matMenuTriggerFor]="tileMenu"
            [matMenuTriggerData]="{ person: person }"
            [attr.aria-label]="'Options for ' + nameOf(person)">
            <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.more" [attr.d]="d"></path></svg>
          </button>
        </div>

        <button type="button" class="tile overflow" *ngIf="layout.overflow as more" [ngStyle]="overflowStyle" (click)="openPanel('people')">
          <strong>+{{ more.count }}</strong>
          <span>more</span>
        </button>

        <div class="strip-pager" *ngIf="layout.pager as pager" [ngStyle]="pagerStyle" role="group" aria-label="Participant pages">
          <button type="button" (click)="pageStrip(-1)" [disabled]="pager.page === 0" aria-label="Previous participants">
            <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.chevronLeft" [attr.d]="d"></path></svg>
          </button>
          <span class="pager-count" aria-live="polite">{{ pager.page + 1 }}/{{ pager.pages }}</span>
          <button type="button" (click)="pageStrip(1)" [disabled]="pager.page >= pager.pages - 1" aria-label="Next participants">
            <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.chevronRight" [attr.d]="d"></path></svg>
          </button>
        </div>

        <div class="connecting" *ngIf="!people.length && !content && !error">
          <span class="spinner"></span>
          <span>Connecting to the meeting…</span>
        </div>

        <div class="stage-error" *ngIf="error">
          <mat-icon>error_outline</mat-icon>
          <strong>{{ error }}</strong>
          <button type="button" (click)="leave()">Leave</button>
        </div>

        <div class="alone-card" *ngIf="isAlone && !error">
          <strong>Waiting for others to join…</strong>
          <span>Invite people by sharing the meeting link.</span>
          <button type="button" (click)="copyLink()">
            <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.link" [attr.d]="d"></path></svg>
            Copy meeting link
          </button>
        </div>

        <button type="button" class="show-self" *ngIf="selfViewHidden && (remoteCount || content)" (click)="toggleSelfView()">
          <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.eye" [attr.d]="d"></path></svg>
          Show self view
        </button>

        <div class="captions" *ngIf="captionsOn" aria-live="polite">
          <p *ngFor="let line of captions; trackBy: trackLine"><strong>{{ line.speaker }}</strong>{{ line.text }}</p>
          <p class="partial" *ngIf="partial"><strong>{{ partial.speaker }}</strong>{{ partial.text }}</p>
          <p class="hint" *ngIf="!captions.length && !partial">Live captions are on. Speech will appear here.</p>
        </div>

        <div class="reaction-layer" aria-hidden="true">
          <span class="float" *ngFor="let item of floating; trackBy: trackById" [style.left.%]="item.left">{{ item.emoji }}</span>
        </div>

        <div class="toasts" aria-live="polite">
          <div class="mtg-toast" *ngFor="let toast of toasts; trackBy: trackById" [class.clickable]="toast.open" (click)="openToast(toast)">
            <mat-icon>{{ toast.icon }}</mat-icon>
            <div class="toast-text">
              <strong>{{ toast.title }}</strong>
              <span *ngIf="toast.text">{{ toast.text }}</span>
            </div>
            <button type="button" class="icon-btn" (click)="$event.stopPropagation(); dismissToast(toast)" aria-label="Dismiss">
              <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.close" [attr.d]="d"></path></svg>
            </button>
          </div>
        </div>
      </div>
    </main>

    <!-- Kept in the DOM (hidden) once Neo.ai has opened, so its conversation survives closing the panel. -->
    <aside class="panel" *ngIf="panel || neoStarted" [class.parked]="!panel" [attr.aria-label]="panelTitle || null">
      <div class="panel-head">
        <h2>{{ panelTitle }}</h2>
        <button type="button" class="icon-btn" (click)="panel = null" aria-label="Close panel">
          <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.close" [attr.d]="d"></path></svg>
        </button>
      </div>
      <div class="panel-body" [ngSwitch]="panel">
        <app-meeting-chat *ngSwitchCase="'chat'" [messages]="messages" (send)="sendChat($event)"></app-meeting-chat>

        <app-meeting-people
          *ngSwitchCase="'people'"
          [people]="people"
          [names]="names"
          [localName]="displayName"
          [pinnedId]="pinnedId"
          (pin)="pin($event)"
          (mute)="muteParticipant($event)"
          (muteAll)="muteAll()"
          (copyLink)="copyLink()">
        </app-meeting-people>

        <div class="neo-slot" *ngIf="neoStarted" [class.parked]="panel !== 'neo'">
          <app-meeting-neo [userName]="displayName" [meetingTitle]="title"></app-meeting-neo>
        </div>

        <app-meeting-transcript
          *ngSwitchCase="'transcript'"
          [lines]="lines"
          [partial]="partial"
          [enabled]="transcriptionEnabled"
          [notice]="transcriptMessage">
        </app-meeting-transcript>

        <div class="scroll" *ngSwitchCase="'effects'">
          <app-meeting-device-settings
            [showDevices]="false"
            [showEffects]="true"
            [headings]="false"
            [blurSupported]="blurSupported"
            [blur]="blur"
            (blurChange)="setBlur($event)">
          </app-meeting-device-settings>
          <p class="panel-note" *ngIf="blurSupported && !cameraOn">Turn your camera on to see the effect.</p>
        </div>

        <div class="scroll info" *ngSwitchCase="'info'">
          <h3>{{ title }}</h3>
          <dl>
            <div *ngIf="publicCode">
              <dt>Meeting code</dt>
              <dd class="code">{{ publicCode }}</dd>
            </div>
            <div *ngIf="hostEmail">
              <dt>Organizer</dt>
              <dd>{{ hostEmail }}</dd>
            </div>
            <div *ngIf="elapsed">
              <dt>Duration</dt>
              <dd>{{ elapsed }}</dd>
            </div>
          </dl>
          <button type="button" class="primary" *ngIf="joinUrl" (click)="copyLink()">
            <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.link" [attr.d]="d"></path></svg>
            Copy join link
          </button>

          <h4>Keyboard shortcuts</h4>
          <ul class="shortcuts">
            <li><span>Mute or unmute</span><kbd>Ctrl+Shift+M</kbd></li>
            <li *ngIf="!audioOnly"><span>Turn camera on or off</span><kbd>Ctrl+Shift+O</kbd></li>
            <li><span>Raise or lower your hand</span><kbd>Ctrl+Shift+K</kbd></li>
            <li *ngIf="canShare"><span>Share your screen</span><kbd>Ctrl+Shift+E</kbd></li>
            <li><span>Leave the meeting</span><kbd>Ctrl+Shift+H</kbd></li>
          </ul>
        </div>
      </div>
    </aside>
  </div>

  <div class="dock">
    <div class="toolbar-wrap">
      <nav class="toolbar" aria-label="Meeting controls">
        <button type="button" class="tb" [class.on]="panel === 'chat'" [attr.aria-pressed]="panel === 'chat'" (click)="togglePanel('chat')" matTooltip="Chat">
          <span class="tb-icon">
            <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.chat" [attr.d]="d"></path></svg>
            <span class="badge unread" *ngIf="unreadChat">{{ unreadChat > 9 ? "9+" : unreadChat }}</span>
          </span>
          <span class="tb-label">Chat</span>
        </button>

        <button type="button" class="tb" [class.on]="panel === 'people'" [attr.aria-pressed]="panel === 'people'" (click)="togglePanel('people')" matTooltip="People">
          <span class="tb-icon">
            <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.people" [attr.d]="d"></path></svg>
            <span class="badge count" *ngIf="panel !== 'people'">{{ people.length || 1 }}</span>
            <span class="hand-flag" *ngIf="raisedCount" [attr.aria-label]="raisedCount + ' raised hand' + (raisedCount > 1 ? 's' : '')">✋</span>
          </span>
          <span class="tb-label">People</span>
        </button>

        <span class="divider" aria-hidden="true"></span>

        <button type="button" class="tb neo" [class.on]="panel === 'neo'" [attr.aria-pressed]="panel === 'neo'" (click)="togglePanel('neo')" matTooltip="Neo.ai">
          <span class="tb-icon">
            <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.sparkles" [attr.d]="d"></path></svg>
          </span>
          <span class="tb-label">Neo</span>
        </button>

        <ng-container *ngIf="!compact">
          <button type="button" class="tb" [matMenuTriggerFor]="reactMenu" matTooltip="React">
            <span class="tb-icon">
              <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.smile" [attr.d]="d"></path></svg>
            </span>
            <span class="tb-label">React</span>
          </button>

          <button type="button" class="tb" *ngIf="!audioOnly" [matMenuTriggerFor]="viewMenu" matTooltip="View">
            <span class="tb-icon">
              <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.grid" [attr.d]="d"></path></svg>
            </span>
            <span class="tb-label">View</span>
          </button>
        </ng-container>

        <button type="button" class="tb" [matMenuTriggerFor]="moreMenu" matTooltip="More actions">
          <span class="tb-icon">
            <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.more" [attr.d]="d"></path></svg>
          </span>
          <span class="tb-label">More</span>
        </button>

        <span class="divider" aria-hidden="true"></span>

        <div class="split" *ngIf="!audioOnly">
          <button
            type="button"
            class="tb"
            [class.off]="!cameraOn"
            [attr.aria-pressed]="cameraOn"
            (click)="camera()"
            [matTooltip]="(cameraOn ? 'Turn camera off' : 'Turn camera on') + ' (Ctrl+Shift+O)'">
            <span class="tb-icon">
              <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of (cameraOn ? icons.video : icons.videoOff)" [attr.d]="d"></path></svg>
            </span>
            <span class="tb-label">Camera</span>
          </button>
          <button type="button" class="tb-caret" [matMenuTriggerFor]="cameraMenu" (menuOpened)="loadDevices()" aria-label="Camera options">
            <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.chevron" [attr.d]="d"></path></svg>
          </button>
        </div>

        <div class="split">
          <button
            type="button"
            class="tb"
            [class.off]="muted"
            [attr.aria-pressed]="!muted"
            (click)="mute()"
            [matTooltip]="(muted ? 'Unmute' : 'Mute') + ' (Ctrl+Shift+M)'">
            <span class="tb-icon">
              <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of (muted ? icons.micOff : icons.mic)" [attr.d]="d"></path></svg>
            </span>
            <span class="tb-label">Mic</span>
          </button>
          <button type="button" class="tb-caret" [matMenuTriggerFor]="micMenu" (menuOpened)="loadDevices()" aria-label="Audio options">
            <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.chevron" [attr.d]="d"></path></svg>
          </button>
        </div>

        <button
          type="button"
          class="tb"
          *ngIf="canShare && !compact"
          [class.on]="sharing"
          [attr.aria-pressed]="sharing"
          (click)="share()"
          [matTooltip]="(sharing ? 'Stop presenting' : 'Share content') + ' (Ctrl+Shift+E)'">
          <span class="tb-icon">
            <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of (sharing ? icons.shareStop : icons.share)" [attr.d]="d"></path></svg>
          </span>
          <span class="tb-label">{{ sharing ? "Stop" : "Share" }}</span>
        </button>

        <span class="divider" aria-hidden="true"></span>

        <!-- The host gets a choice (leaveMenu); everyone else leaves on click. -->
        <button
          type="button"
          class="leave"
          [matMenuTriggerFor]="isHost ? leaveMenu : null"
          (click)="!isHost && leave()"
          [disabled]="leaving"
          [matTooltip]="isHost ? 'Leave or end the meeting' : 'Leave (Ctrl+Shift+H)'"
          [attr.aria-label]="isHost ? 'Leave or end the meeting' : 'Leave call'">
          <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.phoneOff" [attr.d]="d"></path></svg>
          <span class="leave-label">{{ leaving ? (endingForAll ? "Ending…" : "Leaving…") : "Leave Call" }}</span>
          <svg class="ico leave-caret" *ngIf="isHost" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.chevron" [attr.d]="d"></path></svg>
        </button>
      </nav>

      <div class="device-card" *ngIf="devicesOpen" #deviceCard role="dialog" aria-label="Device settings">
        <app-meeting-device-settings
          variant="card"
          [mics]="deviceLists.mics"
          [speakers]="deviceLists.speakers"
          [cameras]="deviceLists.cameras"
          [audioInputId]="current.audioInputId"
          [audioOutputId]="current.audioOutputId"
          [videoInputId]="current.videoInputId"
          [canChooseSpeaker]="canChooseSpeaker"
          [showCamera]="!audioOnly"
          [audioDisabled]="audioOff"
          [level]="audioOff ? null : localLevel"
          (micChange)="chooseMic($event)"
          (speakerChange)="chooseSpeaker($event)"
          (cameraChange)="chooseCamera($event)"
          (done)="closeDevices()">
        </app-meeting-device-settings>
      </div>
    </div>
  </div>
</div>

<mat-menu #leaveMenu="matMenu" class="teams-menu toolbar-pop leave-pop" yPosition="above" xPosition="before">
  <button mat-menu-item (click)="leave()">
    <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.exit" [attr.d]="d"></path></svg>
    <span class="leave-option">
      <b>Leave meeting</b>
      <small>The meeting continues without you</small>
    </span>
  </button>
  <div class="menu-divider"></div>
  <button mat-menu-item class="leave-end" (click)="endForAll()">
    <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.phoneOff" [attr.d]="d"></path></svg>
    <span class="leave-option">
      <b>End meeting for all</b>
      <small>Ends it for everyone and emails the AI summary</small>
    </span>
  </button>
</mat-menu>

<mat-menu #reactMenu="matMenu" class="teams-menu toolbar-pop" yPosition="above">
  <div class="reaction-row">
    <button
      type="button"
      class="reaction-btn"
      *ngFor="let reaction of reactions"
      (click)="react(reaction.type)"
      [attr.aria-label]="reaction.label"
      [matTooltip]="reaction.label">
      {{ reaction.emoji }}
    </button>
  </div>
  <div class="menu-divider"></div>
  <button mat-menu-item (click)="toggleHand()">
    <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.hand" [attr.d]="d"></path></svg>
    <span>{{ handRaisedAt ? "Lower Hand" : "Raise Hand" }}</span>
  </button>
</mat-menu>

<mat-menu #viewMenu="matMenu" class="teams-menu toolbar-pop" yPosition="above">
  <button mat-menu-item (click)="setView('gallery')">
    <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.grid" [attr.d]="d"></path></svg>
    <span>Gallery</span>
    <svg class="ico trail" *ngIf="view === 'gallery' && !pinnedId" viewBox="0 0 24 24" aria-label="Selected" role="img" focusable="false"><path *ngFor="let d of icons.check" [attr.d]="d"></path></svg>
  </button>
  <button mat-menu-item (click)="setView('speaker')">
    <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.user" [attr.d]="d"></path></svg>
    <span>Speaker</span>
    <svg class="ico trail" *ngIf="view === 'speaker' || pinnedId" viewBox="0 0 24 24" aria-label="Selected" role="img" focusable="false"><path *ngFor="let d of icons.check" [attr.d]="d"></path></svg>
  </button>
  <button mat-menu-item [disabled]="!content" (click)="setView('focus')">
    <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.focus" [attr.d]="d"></path></svg>
    <span>Focus on content</span>
    <svg class="ico trail" *ngIf="view === 'focus' && content" viewBox="0 0 24 24" aria-label="Selected" role="img" focusable="false"><path *ngFor="let d of icons.check" [attr.d]="d"></path></svg>
  </button>
  <div class="menu-divider"></div>
  <button mat-menu-item (click)="toggleSelfView()">
    <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of (selfViewHidden ? icons.eye : icons.eyeOff)" [attr.d]="d"></path></svg>
    <span>{{ selfViewHidden ? "Show self view" : "Hide self view" }}</span>
  </button>
</mat-menu>

<mat-menu #moreMenu="matMenu" class="teams-menu toolbar-pop" yPosition="above">
  <ng-container *ngIf="compact">
    <button mat-menu-item [matMenuTriggerFor]="reactMenu">
      <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.smile" [attr.d]="d"></path></svg>
      <span>React</span>
    </button>
    <button mat-menu-item *ngIf="!audioOnly" [matMenuTriggerFor]="viewMenu">
      <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.grid" [attr.d]="d"></path></svg>
      <span>View</span>
    </button>
    <button mat-menu-item *ngIf="canShare" (click)="share()">
      <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of (sharing ? icons.shareStop : icons.share)" [attr.d]="d"></path></svg>
      <span>{{ sharing ? "Stop presenting" : "Share screen" }}</span>
    </button>
    <div class="menu-divider"></div>
  </ng-container>
  <button mat-menu-item (click)="openPanel('info')">
    <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.keyboard" [attr.d]="d"></path></svg>
    <span>Keyboard shortcuts</span>
  </button>
  <button mat-menu-item (click)="toggleFullscreen()">
    <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of (fullscreen ? icons.fullscreenExit : icons.fullscreen)" [attr.d]="d"></path></svg>
    <span>{{ fullscreen ? "Exit full screen" : "Full screen" }}</span>
  </button>
  <button mat-menu-item (click)="openPanel('transcript')">
    <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.transcript" [attr.d]="d"></path></svg>
    <span>Transcript</span>
  </button>
  <div class="menu-divider"></div>
  <button mat-menu-item [disabled]="!transcriptionEnabled" (click)="toggleCaptions()">
    <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.captions" [attr.d]="d"></path></svg>
    <span>{{ captionsOn ? "Turn off live captions" : "Turn on live captions" }}</span>
  </button>
  <button mat-menu-item *ngIf="!audioOnly" (click)="openPanel('effects')">
    <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.sparkle" [attr.d]="d"></path></svg>
    <span>Video effects</span>
  </button>
  <div class="menu-divider"></div>
  <button mat-menu-item (click)="openDevices()">
    <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.settings" [attr.d]="d"></path></svg>
    <span>Device settings</span>
  </button>
  <button mat-menu-item (click)="openPanel('info')">
    <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.info" [attr.d]="d"></path></svg>
    <span>Meeting info</span>
  </button>
</mat-menu>

<mat-menu #cameraMenu="matMenu" class="teams-menu toolbar-pop" yPosition="above">
  <div class="menu-heading">Camera</div>
  <button mat-menu-item *ngFor="let device of deviceLists.cameras; let i = index" (click)="chooseCamera(device.deviceId)">
    <svg class="ico check" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <ng-container *ngIf="device.deviceId === current.videoInputId"><path *ngFor="let d of icons.check" [attr.d]="d"></path></ng-container>
    </svg>
    <span>{{ device.label || "Camera " + (i + 1) }}</span>
  </button>
  <div class="menu-empty" *ngIf="!deviceLists.cameras.length">No cameras found</div>
  <div class="menu-divider"></div>
  <button mat-menu-item (click)="openPanel('effects')">
    <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.sparkle" [attr.d]="d"></path></svg>
    <span>Video effects</span>
  </button>
  <button mat-menu-item (click)="openDevices()">
    <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.settings" [attr.d]="d"></path></svg>
    <span>Device settings</span>
  </button>
</mat-menu>

<mat-menu #micMenu="matMenu" class="teams-menu toolbar-pop" yPosition="above">
  <div class="menu-heading">Microphone</div>
  <button mat-menu-item *ngFor="let device of deviceLists.mics; let i = index" [disabled]="audioOff" (click)="chooseMic(device.deviceId)">
    <svg class="ico check" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <ng-container *ngIf="device.deviceId === current.audioInputId"><path *ngFor="let d of icons.check" [attr.d]="d"></path></ng-container>
    </svg>
    <span>{{ device.label || "Microphone " + (i + 1) }}</span>
  </button>
  <div class="menu-empty" *ngIf="!deviceLists.mics.length">No microphones found</div>
  <ng-container *ngIf="canChooseSpeaker && deviceLists.speakers.length">
    <div class="menu-divider"></div>
    <div class="menu-heading">Speaker</div>
    <button mat-menu-item *ngFor="let device of deviceLists.speakers; let i = index" [disabled]="audioOff" (click)="chooseSpeaker(device.deviceId)">
      <svg class="ico check" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <ng-container *ngIf="device.deviceId === (current.audioOutputId || deviceLists.speakers[0].deviceId)"><path *ngFor="let d of icons.check" [attr.d]="d"></path></ng-container>
      </svg>
      <span>{{ device.label || "Speaker " + (i + 1) }}</span>
    </button>
  </ng-container>
  <div class="menu-divider"></div>
  <button mat-menu-item (click)="openDevices()">
    <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.settings" [attr.d]="d"></path></svg>
    <span>Device settings</span>
  </button>
</mat-menu>

<mat-menu #tileMenu="matMenu" class="teams-menu" xPosition="before">
  <ng-template matMenuContent let-person="person">
    <button mat-menu-item *ngIf="!person.local && pinnedId !== person.attendeeId" (click)="pin(person.attendeeId)">
      <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.pin" [attr.d]="d"></path></svg>
      <span>Pin for me</span>
    </button>
    <button mat-menu-item *ngIf="pinnedId === person.attendeeId" (click)="pin(null)">
      <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.pin" [attr.d]="d"></path></svg>
      <span>Unpin</span>
    </button>
    <button mat-menu-item *ngIf="!person.local" [disabled]="person.muted === true" (click)="muteParticipant(person.attendeeId)">
      <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.micOff" [attr.d]="d"></path></svg>
      <span>Mute</span>
    </button>
    <button mat-menu-item *ngIf="person.local" (click)="toggleSelfView()">
      <svg class="ico" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path *ngFor="let d of icons.eyeOff" [attr.d]="d"></path></svg>
      <span>Hide for me</span>
    </button>
  </ng-template>
</mat-menu>
